import axios from 'axios';
import { Form, Formik } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { REDIRECT_SHIPMENT } from '../../constants/paths/api';
import usePostCodes from '../../hooks/usePostCodes';
import useRouter from '../../hooks/useRouter/useRouter';
import IChangeDestination from '../../interfaces/changeDestination/changeDestination';
import Button from '../base/button';
import FormikFieldWrapper from '../base/forms/formikFieldWrapper';
import FormikSelect from '../base/forms/formikSelect';
import { StyledFormikFastField } from '../base/forms/input';
import { Column, Columns } from '../base/grid';
import RichTextTitle from '../base/richTextTitle';
import { P } from '../base/typography';
import { RawLoader } from '../loading/loading';
import { ChangeDestinationFormSchema } from './changeDestinationForm.schema';

interface Props {
	values: IChangeDestination;
	errors: any;
	setFieldTouched: any;
	setFieldValue: any;
	isSubmitting: any;
	isValid: any;
}

const ChangeDestinationForm: FC<Props> = () => {
	const { t } = useTranslation(['generalForm', 'forms']);
	const [errorMessage, setErrorMessage] = useState('');
	const [submitSuccess, setSubmitSuccess] = useState(false);
	const [inputDisabled, setInputDisabled] = useState(false);
	const [loading, setLoading] = useState(false);
	const { location } = useRouter();

	const { postCodes } = usePostCodes();

	// Initialize `initialValues` with a state variable
	const [initialValues, setInitialValues] = useState<IChangeDestination>({
		shipmentId: '',
		email: '',
		mobileNumber: '',
		deliveryChoice: null,
		additionalText: '',
		postalCode: null,
	});

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const sParam = params.get('s');
		if (sParam) {
			setInputDisabled(true);
			setInitialValues(prevValues => ({
				...prevValues,
				shipmentId: sParam,
			}));
		}
	}, [location.search]);

	const selectOptions = [
		{ value: 'HomeDelivery', label: 'Ég vil fá pakkann sendan í heimsendingu, verð 590 kr.' },
		{ value: 'PostOffice', label: 'Ég vil fá pakkann sendan á pósthús.' },
	];

	const handleSubmit = async (formValues, { setSubmitting, resetForm }) => {
		if (formValues.deliveryChoice.value === 'PostOffice' && !formValues.postalCode) {
			setErrorMessage('Vinsamlegast veldu pósthús');
			return;
		}
		if (formValues.deliveryChoice.value === 'HomeDelivery' && !formValues.additionalText) {
			setErrorMessage('Vinsamlegast sláðu inn heimilisfang');
			return;
		}

		setLoading(true);
		const body = {
			shipmentId: formValues.shipmentId,
			email: formValues.email,
			mobileNumber: formValues.mobileNumber,
			deliveryChoice: formValues.deliveryChoice.value,
			additionalText: formValues.additionalText,
			postalCode: formValues.postalCode.value,
		};

		try {
			await axios.post(`${REDIRECT_SHIPMENT}`, body);
			setSubmitSuccess(true);
			setErrorMessage('');
			resetForm();
		} catch (error) {
			setErrorMessage(error.response?.data?.details || 'An error occurred');
			setSubmitSuccess(false);
		} finally {
			setSubmitting(false);
			setLoading(false);
		}
	};

	const postOfficeOptions = [
		{ value: '108', label: 'Pósthús Síðumúla' },
		{ value: '110', label: 'Pósthús Stórhöfða' },
		{ value: '200', label: 'Pósthús Dalvegi Kópavogi' },
	];

	return (
		<Container>
			<StyledChangeDestinationForm>
				<Columns>
					<Formik
						initialValues={initialValues}
						enableReinitialize
						onSubmit={handleSubmit}
						validationSchema={ChangeDestinationFormSchema}>
						{({ setFieldValue, values }) => {
							// Watch for changes in deliveryChoice and clear postalCode if deliveryChoice changes
							useEffect(() => {
								// Clear postalCode when deliveryChoice changes
								setFieldValue('postalCode', null);
							}, [values.deliveryChoice, setFieldValue]);

							return (
								<Form>
									<ShadowColumn>
										<Column>
											<RichTextTitle
												content="<strong>Hvernig</strong> viltu fá sendinguna þína afhenta?"
												type="h4"
											/>
										</Column>
										<Column>
											<FormikFieldWrapper
												name="shipmentId"
												label="Sendingarnúmer"
												languageComponent="">
												<BRFormikFastField
													disabled={inputDisabled}
													name="shipmentId"
													placeholder="Sendingarnúmer"
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
														const uppercaseValue = e.target.value.toUpperCase();
														setFieldValue('shipmentId', uppercaseValue);
													}}
												/>
											</FormikFieldWrapper>
										</Column>
										<Column>
											<FormikFieldWrapper
												name="deliveryChoice"
												label="Hvernig viltu fá sendinguna þína afhenta?"
												languageComponent="">
												<BRFormikSelect
													id="deliveryChoice"
													name="deliveryChoice"
													options={selectOptions}
													value={values.deliveryChoice}
													onBlur={() => {}}
													onChange={setFieldValue}
													placeholder="Veldu afhendingarstað"
												/>
											</FormikFieldWrapper>
										</Column>
									</ShadowColumn>
									{values.deliveryChoice && values.deliveryChoice.value === 'HomeDelivery' && (
										<StyledDiv>
											<Columns>
												<Column>
													<RichTextTitle
														content="<strong>Hvert</strong> eigum við að senda?"
														type="h4"
													/>
												</Column>
												<Column>
													<P>
														Á hvaða heimilisfang eigum við að afhenda sendinguna?
														Vinsamlegast gefðu upp fullt heimilisfang og aðrar leiðbeiningar
														eins og á við (t.d. bakhús, kjallari o.s.frv.).
													</P>
												</Column>

												<Column tablet={3 / 5}>
													<FormikFieldWrapper name="additionalText" languageComponent="">
														<BRFormikFastField
															type="text"
															id="additionalText"
															name="additionalText"
															padding="1rem"
															placeholder="Sláðu inn heimilisfang"
														/>
													</FormikFieldWrapper>
												</Column>
												<Column tablet={2 / 5}>
													<FormikFieldWrapper name="postalCode" label="" languageComponent="">
														<BRFormikSelect
															id="postalCode"
															name="postalCode"
															options={postCodes}
															value={values.postalCode}
															onBlur={() => {}}
															onChange={setFieldValue}
															placeholder="Veldu póstnúmer"
														/>
													</FormikFieldWrapper>
												</Column>
											</Columns>
										</StyledDiv>
									)}
									{values.deliveryChoice && values.deliveryChoice.value === 'PostOffice' && (
										<StyledDiv>
											<Column>
												<RichTextTitle
													content="Á hvaða <strong>pósthús</strong> vilt þú sækja?"
													type="h4"
												/>
											</Column>
											<Column>
												<FormikFieldWrapper name="postalCode" label="" languageComponent="">
													<BRFormikSelect
														id="postalCode"
														name="postalCode"
														options={postOfficeOptions}
														value={values.postalCode}
														onBlur={() => {}}
														onChange={setFieldValue}
														placeholder="Veldu afhendingarstað"
													/>
												</FormikFieldWrapper>
											</Column>
										</StyledDiv>
									)}
									{errorMessage && <P>{errorMessage}</P>}
									{submitSuccess && <P>Takk fyrir! Beiðni móttekin.</P>}
									<StyledColumn>
										{loading ? (
											<RawLoader />
										) : (
											<BRButton wide primary type="submit">
												{t('submit')}
											</BRButton>
										)}
									</StyledColumn>
								</Form>
							);
						}}
					</Formik>
				</Columns>
			</StyledChangeDestinationForm>
		</Container>
	);
};

export default ChangeDestinationForm;

const StyledChangeDestinationForm = styled.div`
	width: 55%;
	@media (max-width: 768px) {
		width: 100%;
	}
`;

const StyledColumn = styled(Column)`
	padding: 1rem;
	margin-top: 1rem;
	display: flex;
	justify-content: right;
`;

const Container = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
`;

const StyledDiv = styled.div`
	margin-top: 2rem;
	padding: 2rem;
	box-shadow: 0px 4px 8px rgba(0.1, 0, 0.1, 0.1);
	border-radius: 1rem;
`;

const ShadowColumn = styled.div`
	box-shadow: 0px 4px 8px rgba(0.1, 0, 0.1, 0.1);
	padding-top: 1rem;
	padding-bottom: 1rem;
	border-radius: 1rem;
`;

const BRFormikFastField = styled(StyledFormikFastField)`
	border-radius: 0.5rem;
`;

const BRFormikSelect = styled(FormikSelect)`
	border-radius: 0.5rem;
`;

/* const BRStyledFormikTextArea = styled(StyledFormikTextArea)`
	border-radius: 0.5rem;
`; */

const BRButton = styled(Button)`
	border-radius: 1rem;
`;
